// src/components/DeliveryProcess.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaAngleRight,
  FaBarcode, // Chassis Print
  FaFileAlt, // Documents
  FaTractor, // Agriculture
  FaHandHoldingUsd, // Subsidy
  FaSearch, // Parsing Documents
  FaCheckCircle, // Parsing Done
  FaIdCard, // Number Plate Order
  FaTruckMoving, // Dhrangadhra (Dispatch)
  FaWrench, // Fitmate (Setup/Inspection)
  FaKey, // Delivery (Handover)
  FaTasks, // Default / Fallback
} from "react-icons/fa";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import translations from "../../assets/locals/translations";
import "./DeliveryProcess.css";
import InventoryServices from "../../services/InventoryServices";

const steps = [
  {
    titleKey: "chassisPrintTitle",
    detailsKey: "chassisPrintDetails",
    stepKey: "chassis_print",
    icon: FaBarcode,
    iconBgColor: "#fff0f0",
    iconColor: "#ff6b6b",
  },
  {
    titleKey: "documentsTitle",
    detailsKey: "documentsDetails",
    stepKey: "documents",
    icon: FaFileAlt,
    iconBgColor: "#e6f7ff",
    iconColor: "#1890ff",
  },
  // {
  //   title: "PARSING TYPE", // Shortened Title
  //   stepKey: "parsing_type",
  //   details: "Check temporary parsing_type status.",
  //   icon: FaTractor,
  //   iconBgColor: "#f6ffed", // Light Green
  //   iconColor: "#52c41a", // Green
  // },
  // {
  //   title: "SUBSIDY PROCESS",
  //   stepKey: "subsidy",
  //   details: "Verify subsidy application and RTO paper submission.",
  //   icon: FaHandHoldingUsd,
  //   iconBgColor: "#fffbe6", // Light Yellow
  //   iconColor: "#faad14", // Yellow
  // },
  {
    titleKey: "parsingPendingTitle",
    detailsKey: "parsingPendingDetails",
    stepKey: "parsing_complete",
    icon: FaCheckCircle,
    iconBgColor: "#f6ffed",
    iconColor: "#52c41a",
  },
  {
    titleKey: "plateOrderTitle",
    detailsKey: "plateOrderDetails",
    stepKey: "plate_order",
    icon: FaIdCard,
    iconBgColor: "#f0f5ff",
    iconColor: "#2f54eb",
  },
  {
    titleKey: "rcFitmentTitle",
    detailsKey: "rcFitmentDetails",
    stepKey: "rc_fitment",
    icon: FaWrench,
    iconBgColor: "#f9f0ff",
    iconColor: "#722ed1",
  },
  {
    titleKey: "rcDispatchTitle",
    detailsKey: "rcDispatchDetails",
    stepKey: "rc_dispatch",
    icon: FaTruckMoving,
    iconBgColor: "#fff1f0",
    iconColor: "#fa541c",
  },
   {
    titleKey: "number22Title",
    detailsKey: "number22Details",
    stepKey: "number22",
    icon: FaTruckMoving,
    iconBgColor: "#fff1f0", // Light Orange
    iconColor: "#fa541c", // Orange
  },
  // {
  //   title: "FINAL DELIVERY",
  //   stepKey: "delivery",
  //   details: "Final check of vehicle delivery to customer.",
  //   icon: FaKey,
  //   iconBgColor: "#fcffe6", // Light Lime
  //   iconColor: "#a0d911", // Lime
  // },
];

export default function DeliveryProcessBoard() {
  const navigate = useNavigate();
  const InventoryService = new InventoryServices();
  const [loading, setLoading] = useState(false);
  const [stepCount, setStepCount] = useState({
    chassis_print: 0,
    documents: 0,
    parsing_type: 0,
    parse_documents: 0,
    parsing_complete: 0,
    plate_order: 0,
    rc_dispatch: 0,
    rc_fitment: 0,
    delivery: 0,
    number22: 0,
  });
  // Count of billing logs that don't have 22 number yet (show in 22 Number card until they add it)
  const [billingLogsWithoutNumber22Count, setBillingLogsWithoutNumber22Count] = useState(0);

  const getDeliveryDataLogCounts = async () => {
    setLoading(true);
    try {
      const [countsRes, billingRes] = await Promise.all([
        InventoryService.getDeliveryDataLogCounts(),
        InventoryService.getBillingLogs(),
      ]);
      if (countsRes && countsRes.isSuccess) {
        const [counts] = countsRes.result || [stepCount];
        setStepCount((prev) => ({ ...prev, ...counts }));
      }
      // Count billing logs that don't have 22 number (empty/null) – show in 22 Number card
      if (billingRes && billingRes.isSuccess && Array.isArray(billingRes.result)) {
        const without22 = billingRes.result.filter((log) => {
          const num = log.number22 ?? log.number_22 ?? "";
          return num === null || num === undefined || String(num).trim() === "";
        });
        setBillingLogsWithoutNumber22Count(without22.length);
      }
    } catch (error) {
      console.error(error, "Error getting stocks list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDeliveryDataLogCounts();
  }, []);

  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];

  const handleStepClick = (index) => {
    const step = steps[index];
    const formattedKey = step.stepKey; // keep URL stable using stepKey
    console.log(`Step ${index} clicked`, formattedKey);
    navigate(`/delivery/steps/${formattedKey}`, {
      state: {
        stepKey: step.stepKey,
        stepIndex: index,
        stepTitle: t[step.titleKey] || step.titleKey,
      },
    });
  };

  return (
    <div className="delivery-board-wrapper">
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mr: 2 }}>
          {t.deliveryPipeline || "Delivery Pipeline"}
        </Typography>
        <Box sx={{ flexGrow: 1, borderBottom: "1px solid #ccc" }} />
      </Box>

      {/* Grid container for the step cards */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="delivery-steps-grid">
          {steps.map((step, index) => {
            // 22 Number card: show count of billing logs that don't have 22 number yet; once added, they drop from this count
            const count =
              step.stepKey === "number22"
                ? billingLogsWithoutNumber22Count
                : (stepCount[step.stepKey] ?? 0);
            // Get the specific icon for this step, with a fallback
            const StepIconComponent = step.icon || FaTasks;

            return (
              <div
                key={step.stepKey || index} // Use step as key if available
                className="delivery-card"
                onClick={() => handleStepClick(index)}
              >
                {/* Top Section: Icon & Text */}
                <div className="delivery-card-top">
                  <div
                    className="delivery-card-icon-wrapper"
                    style={{ backgroundColor: step.iconBgColor || "#e0e0e0" }} // Use specific or default BG
                  >
                    <StepIconComponent
                      className="delivery-card-icon"
                      style={{ color: step.iconColor || "#5f6368" }} // Use specific or default color
                    />
                  </div>
                  <div className="delivery-card-text">
                    <h3 className="delivery-step-title">{t[step.titleKey] || step.titleKey}</h3>
                    <p className="delivery-step-details">{t[step.detailsKey] || step.detailsKey}</p>
                  </div>
                </div>

                {/* Bottom Section: Count & Action */}
                <div className="delivery-card-bottom">
                  <div className="delivery-count-display">
                    <span
                      className={`delivery-count-number ${count === 0 ? "zero" : ""
                        }`}
                    >
                      {count}
                    </span>
                    <span className="delivery-count-label">
                      {t.pendingLabel || "PENDING"}
                    </span>
                  </div>
                  <FaAngleRight className="delivery-action-icon" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
